import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { City, availableCities, pricingService, CityPricingResponse, PRICING_CACHE_PREFIX } from '../services/pricingService';

interface CityPricingContextType {
  selectedCity: City | null;
  availableCities: City[];
  cityPricing: CityPricingResponse | null;
  isLoadingPricing: boolean;
  error: string | null;
  setSelectedCity: (city: City) => void;
  refreshPricing: () => void;
}

const CityPricingContext = createContext<CityPricingContextType | undefined>(undefined);

interface CityPricingProviderProps {
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Cache helpers – stale-while-revalidate pattern
// Pricing data is persisted in localStorage so the FIRST render after a
// browser refresh can display real DB prices synchronously, with no flash of
// static fallback prices. A fresh fetch runs in the background and silently
// overwrites the cache for the next visit.
// PRICING_CACHE_PREFIX is exported from pricingService — bump its version
// there whenever DB prices are updated to force cache invalidation.
// ---------------------------------------------------------------------------

const readPricingCache = (cityCode: string): CityPricingResponse | null => {
  try {
    const raw = localStorage.getItem(`${PRICING_CACHE_PREFIX}${cityCode}`);
    return raw ? (JSON.parse(raw) as CityPricingResponse) : null;
  } catch {
    return null;
  }
};

const writePricingCache = (cityCode: string, data: CityPricingResponse): void => {
  try {
    localStorage.setItem(`${PRICING_CACHE_PREFIX}${cityCode}`, JSON.stringify(data));
  } catch {
    // Ignore storage-quota errors
  }
};

export const CityPricingProvider: React.FC<CityPricingProviderProps> = ({ children }) => {
  // ---------------------------------------------------------------------------
  // On every app startup: purge localStorage keys from previous cache versions.
  // This guarantees users never see stale prices after a DB price update + deploy.
  // Add old prefixes here whenever PRICING_CACHE_PREFIX is bumped.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const stalePrefixes = ['cityPricing_v1_'];
    stalePrefixes.forEach(oldPrefix => {
      Object.keys(localStorage)
        .filter(key => key.startsWith(oldPrefix))
        .forEach(key => {
          localStorage.removeItem(key);
          console.log(`🧹 Removed stale pricing cache key: ${key}`);
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialise selectedCity synchronously from localStorage so the very first
  // render has both cityPricing (cache) AND selectedCity ready. Without this,
  // selectedCity is null on render-1 and any component that guards on
  // `selectedCity && cityPricing` will fall back to static prices.
  const [selectedCity, setSelectedCityState] = useState<City | null>(() => {
    const savedCityCode = localStorage.getItem('selectedCityCode');
    if (savedCityCode) {
      return availableCities.find(city => city.code === savedCityCode) || availableCities[0];
    }
    return availableCities[0];
  });

  // Initialise synchronously from localStorage cache.
  // This means the VERY FIRST render already has the correct DB prices when
  // the user refreshes the page – no async wait required.
  // IMPORTANT: fall back to availableCities[0].code when no city has been
  // explicitly saved yet – selectedCity lazy init already does the same, so
  // the cache lookup must use the same key to avoid a null mismatch.
  const [cityPricing, setCityPricing] = useState<CityPricingResponse | null>(() => {
    const savedCode = localStorage.getItem('selectedCityCode');
    const code = savedCode || availableCities[0]?.code;
    return code ? readPricingCache(code) : null;
  });

  // isLoadingPricing is false when we already have cached data so the UI
  // shows real prices immediately. It flips to true only when there is no
  // cache (first-ever visit) so a skeleton is shown instead of wrong prices.
  const [isLoadingPricing, setIsLoadingPricing] = useState<boolean>(() => {
    const savedCode = localStorage.getItem('selectedCityCode');
    const code = savedCode || availableCities[0]?.code;
    return !code || !readPricingCache(code);
  });

  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // Helper function to check if current route is provider-related
  // This prevents unnecessary pricing API calls on provider login/landing pages
  const isProviderRoute = () => {
    return location.pathname.startsWith('/dashboard/provider') || 
           location.pathname.startsWith('/provider/') ||
           location.pathname === '/dashboard/provider';
  };

  // ---------------------------------------------------------------------------
  // Background cache refresh — fires on every mount for all non-active cities.
  // Guarantees that backend price changes are picked up on every page load,
  // not just the first visit.
  //
  // Strategy (stale-while-revalidate for ALL cities):
  //   • Active city  → handled by fetchCityPricing() below (updates React state too)
  //   • Other cities → refreshed here silently, writes ONLY to localStorage,
  //                    so the visible UI is never disrupted
  //
  // Uses Promise.allSettled so a failure on one city never blocks another.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isProviderRoute()) return;

    const refreshNonActiveCaches = async () => {
      // Exclude the active city — fetchCityPricing already handles it
      const activeCityCode = localStorage.getItem('selectedCityCode') || availableCities[0]?.code;
      const citiesToRefresh = availableCities.filter(
        (city) => city.code !== activeCityCode
      );
      if (citiesToRefresh.length === 0) return;

      console.log('🔄 Background cache refresh for non-active cities:', citiesToRefresh.map((c) => c.code));

      await Promise.allSettled(
        citiesToRefresh.map(async (city) => {
          try {
            const pricing = await pricingService.getCityPricing(city.code);
            writePricingCache(city.code, pricing);
            console.log(`✅ Cache refreshed for ${city.code}`);
          } catch (err) {
            // Silent failure — stale cache remains usable; fresh data retried next visit
            console.warn(`⚠️ Background cache refresh failed for ${city.code}:`, err);
          }
        })
      );
    };

    refreshNonActiveCaches();
    // Run once on mount only — exhaustive-deps disabled intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved city from localStorage on mount
  useEffect(() => {
    console.log('🏙️ CityPricingProvider initializing...');
    console.log('🛣️ Current route:', location.pathname);
    
    if (isProviderRoute()) {
      console.log('🚫 Provider route detected - skipping city pricing initialization');
      setIsLoadingPricing(false); // No pricing will be fetched; don't leave UI stuck in loading
      return;
    }
    
    const savedCityCode = localStorage.getItem('selectedCityCode');
    console.log('🏙️ Saved city code:', savedCityCode);
    
    if (savedCityCode) {
      const savedCity = availableCities.find(city => city.code === savedCityCode);
      if (savedCity) {
        console.log('🏙️ Setting saved city:', savedCity);
        setSelectedCityState(savedCity);
      }
    } else {
      // Default to first city if no city is saved
      console.log('🏙️ No saved city, defaulting to first available city');
      setSelectedCityState(availableCities[0]);
    }
  }, [location.pathname]);

  // Fetch pricing when city changes
  useEffect(() => {
    if (selectedCity && !isProviderRoute()) {
      console.log('🏙️ Fetching city pricing for:', selectedCity.name);
      fetchCityPricing(selectedCity.code);
    } else if (isProviderRoute()) {
      console.log('🚫 Provider route - skipping pricing fetch');
    }
  }, [selectedCity, location.pathname]);

  const fetchCityPricing = async (cityCode: string) => {
    const hasCachedData = readPricingCache(cityCode) !== null;
    // Only show loading spinner when there is no cached data to display.
    // When cache exists, the fetch runs silently in the background.
    if (!hasCachedData) {
      setIsLoadingPricing(true);
    }
    setError(null);
    
    try {
      const pricing = await pricingService.getCityPricing(cityCode);
      setCityPricing(pricing);
      // Persist to cache so next refresh is instant
      writePricingCache(cityCode, pricing);
      console.log(`✅ Pricing loaded for ${cityCode}:`, pricing);
    } catch (error) {
      console.error(`❌ Failed to load pricing for ${cityCode}:`, error);
      setError((error as Error).message);
      // Keep the cached data visible on error – don't clear it
      if (!hasCachedData) {
        setCityPricing(null);
      }
    } finally {
      setIsLoadingPricing(false);
    }
  };

  const setSelectedCity = (city: City) => {
    setSelectedCityState(city);
    localStorage.setItem('selectedCityCode', city.code);
  };

  const refreshPricing = () => {
    if (selectedCity) {
      fetchCityPricing(selectedCity.code);
    }
  };

  return (
    <CityPricingContext.Provider
      value={{
        selectedCity,
        availableCities,
        cityPricing,
        isLoadingPricing,
        error,
        setSelectedCity,
        refreshPricing
      }}
    >
      {children}
    </CityPricingContext.Provider>
  );
};

export const useCityPricing = (): CityPricingContextType => {
  const context = useContext(CityPricingContext);
  if (context === undefined) {
    throw new Error('useCityPricing must be used within a CityPricingProvider');
  }
  return context;
};